import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    X, Search, Loader2, ShieldCheck, Sparkles, Phone, Mail, MapPin, Car,
    Users, Building2, Link2, User, CheckCircle2, AlertTriangle, Clock,
    RefreshCw, Download, ChevronRight, Code2, Wallet,
} from 'lucide-react';
import { Negociacao, LemitPessoaData, LemitEmpresaData, LemitTipo, LemitTelefone } from '../../../../types';
import { supabase } from '../../../../src/lib/supabase';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { useToast } from '../../../../src/contexts/ToastContext';
import {
    consultarLemit, getLemitHistory, getLatestLemitConsulta,
    buildImportDiff, applyImport, ImportField, LemitCriterio,
} from '../../../../src/services/lemit';
import { propsFundoModal } from '../../../../src/lib/backdropModal';

// ── Formatadores ──────────────────────────────────────────────────────────────
const formatDocumento = (doc?: string): string => {
    const c = (doc || '').replace(/\D/g, '');
    if (c.length === 11) return c.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    if (c.length === 14) return c.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    return doc || '—';
};
const formatPhone = (p: LemitTelefone): string => {
    const n = (p.numero || '').replace(/\D/g, '');
    const num = n.length >= 9 ? `${n.slice(0, n.length - 4)}-${n.slice(-4)}` : n;
    return `(${p.ddd ?? '--'}) ${num}`;
};
const fmtBRL = (v?: number | null) => v != null ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
const fmtDate = (iso?: string) => {
    if (!iso) return '—';
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
};
const timeAgo = (dateStr?: string): string => {
    if (!dateStr) return '';
    const diffMin = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
    if (diffMin < 1) return 'agora mesmo';
    if (diffMin < 60) return `há ${diffMin} min`;
    const h = Math.floor(diffMin / 60);
    if (h < 24) return `há ${h}h`;
    return `há ${Math.floor(h / 24)} dia(s)`;
};



interface Props {
    isOpen: boolean;
    onClose: () => void;
    negotiation: Negociacao;
    onUpdate: () => void;
}

type ResultState = {
    data: LemitPessoaData | LemitEmpresaData;
    raw: any;
    tipo: LemitTipo;
    source: 'api' | 'cache';
    cached_at: string;
    cached_by?: string | null;
} | null;

// ── Subcomponentes de UI ──────────────────────────────────────────────────────
const card = 'bg-white dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden';
const sectionHead = 'flex items-center gap-2.5 px-5 py-3.5 border-b border-slate-100 dark:border-slate-800';

const SectionCard: React.FC<{ icon: React.ReactNode; title: string; count?: number; tint?: string; children: React.ReactNode; delay?: number }> =
    ({ icon, title, count, tint = 'text-primary bg-primary/8 dark:bg-primary/15', children, delay = 0 }) => (
        <div className={`${card} animate-slide-up`} style={{ animationDelay: `${delay}ms` }}>
            <div className={sectionHead}>
                <div className={`p-1.5 rounded-lg ${tint}`}>{icon}</div>
                <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200">{title}</h4>
                {count != null && count > 0 && (
                    <span className="px-2 py-0.5 text-[10px] font-bold bg-primary/10 text-primary rounded-full">{count}</span>
                )}
            </div>
            <div className="p-4">{children}</div>
        </div>
    );

const InfoCell: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
    <div className="space-y-0.5">
        <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">{label}</p>
        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 break-words">{value || '—'}</p>
    </div>
);

const EmptyRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <p className="text-xs text-slate-400 italic py-2 text-center">{children}</p>
);



const LemitConsultaModal: React.FC<Props> = ({ isOpen, onClose, negotiation, onUpdate }) => {
    const { user, profile } = useAuth();
    const { addToast } = useToast();


    const [valorInput, setValorInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<ResultState>(null);
    const [error, setError] = useState<string | null>(null);
    const [confirming, setConfirming] = useState(false);
    const [forceMode, setForceMode] = useState(false);
    const [history, setHistory] = useState<any[]>([]);
    const [custoPessoa, setCustoPessoa] = useState<number | null>(null);
    const [custoEmpresa, setCustoEmpresa] = useState<number | null>(null);
    const [selected, setSelected] = useState<Record<string, boolean>>({});
    const [importing, setImporting] = useState(false);
    const [showRaw, setShowRaw] = useState(false);

    // Critério da API derivado do input (11 dígitos = CPF, 14 = CNPJ)
    const apiCriterio: LemitCriterio | null = useMemo(() => {
        const d = valorInput.replace(/\D/g, '');
        if (d.length === 11) return 'cpf';
        if (d.length === 14) return 'cnpj';
        return null;
    }, [valorInput]);

    const valorValido = useMemo(() => apiCriterio === 'cpf' || apiCriterio === 'cnpj', [apiCriterio]);

    const resultTipo: LemitTipo = result?.tipo ?? (apiCriterio === 'cnpj' ? 'empresa' : 'pessoa');
    const custo = resultTipo === 'empresa' ? custoEmpresa : custoPessoa;

    const importFields: ImportField[] = useMemo(
        () => (result ? buildImportDiff(negotiation, result.data, result.tipo) : []),
        [result, negotiation],
    );

    // Reset + carregar contexto ao abrir (sem custo)
    useEffect(() => {
        if (!isOpen) return;
        setError(null); setResult(null); setConfirming(false); setForceMode(false); setShowRaw(false);

        setValorInput(negotiation.cpf_cnpj || '');

        let active = true;
        (async () => {
            // custos configurados
            const { data: cfgs } = await supabase.from('system_settings').select('key, value').in('key', ['lemit_custo_pessoa', 'lemit_custo_empresa']);
            if (active && cfgs) {
                cfgs.forEach((c: any) => {
                    const p = parseFloat(String(c.value).replace(',', '.'));
                    if (!isNaN(p)) { if (c.key === 'lemit_custo_pessoa') setCustoPessoa(p); else setCustoEmpresa(p); }
                });
            }
            // se houver documento, exibe a última consulta já feita (sem custo)
            const docDigits = (negotiation.cpf_cnpj || '').replace(/\D/g, '');
            if (docDigits.length === 11 || docDigits.length === 14) {
                const docTipo: LemitTipo = docDigits.length === 14 ? 'empresa' : 'pessoa';
                const [hist, latest] = await Promise.all([
                    getLemitHistory(negotiation.cpf_cnpj || ''),
                    getLatestLemitConsulta(negotiation.cpf_cnpj || '', docTipo),
                ]);
                if (!active) return;
                setHistory(hist);
                if (latest) {
                    setResult({
                        data: latest.resultado, raw: latest.resultado_raw, tipo: latest.tipo,
                        source: 'cache', cached_at: latest.created_at, cached_by: latest.consultado_por_nome,
                    });
                }
            }
        })();
        return () => { active = false; };
    }, [isOpen, negotiation.id]);



    // Inicializa seleção de import quando há resultado
    useEffect(() => {
        if (importFields.length) {
            const init: Record<string, boolean> = {};
            importFields.forEach((f) => { init[f.key] = f.aplicarDefault; });
            setSelected(init);
        }
    }, [importFields]);

    const runConsulta = async (force: boolean) => {
        if (!apiCriterio || !valorValido) { addToast('Preencha um valor válido para o critério selecionado.', 'warning'); return; }
        setConfirming(false);
        setLoading(true);
        setError(null);
        try {
            const r = await consultarLemit({ criterio: apiCriterio, valor: valorInput, negociacaoId: negotiation.id, forceRefresh: force });
            setResult({ data: r.data, raw: r.raw, tipo: r.tipo, source: r.source, cached_at: r.cached_at, cached_by: r.cached_by });
            if (r.documento_resolvido) setHistory(await getLemitHistory(r.documento_resolvido));
            addToast(r.source === 'cache' ? 'Dados recuperados do cache (sem custo).' : 'Consulta Lemit realizada com sucesso!', 'success');
        } catch (e: any) {
            setError(e.message || 'Erro na consulta Lemit');
            addToast(e.message || 'Erro na consulta Lemit', 'error');
        } finally {
            setLoading(false);
        }
    };

    const openConfirm = (force: boolean) => {
        if (!valorValido) { addToast('Preencha um valor válido para o critério selecionado.', 'warning'); return; }
        setForceMode(force); setConfirming(true);
    };

    const handleImport = async () => {
        const chosen = importFields.filter((f) => selected[f.key]);
        if (chosen.length === 0) { addToast('Selecione ao menos um campo para importar.', 'warning'); return; }
        setImporting(true);
        try {
            await applyImport(negotiation.id, chosen, user?.id);
            addToast(`${chosen.length} campo(s) importado(s) para o lead.`, 'success');
            onUpdate();
        } catch (e: any) {
            addToast('Erro ao importar: ' + (e.message || 'erro desconhecido'), 'error');
        } finally {
            setImporting(false);
        }
    };

    if (!isOpen) return null;

    const isEmpresa = resultTipo === 'empresa';
    const pessoa = result && result.tipo === 'pessoa' ? (result.data as LemitPessoaData) : null;
    const empresa = result && result.tipo === 'empresa' ? (result.data as LemitEmpresaData) : null;
    const selectedCount = importFields.filter((f) => selected[f.key]).length;



    return createPortal(
        <div className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 animate-fade-in" {...propsFundoModal(() => onClose())}>
            <div className="bg-gray-50 dark:bg-slate-950 rounded-2xl w-full max-w-4xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="relative px-5 sm:px-6 py-4 bg-gradient-to-r from-[#5F6C37] to-[#3f4a24] text-white flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="p-2.5 bg-white/15 rounded-xl backdrop-blur-sm">
                            <Sparkles size={20} />
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-lg font-extrabold leading-tight">Enriquecimento Lemit</h2>
                            <p className="text-[11px] text-white/80 font-medium">Busca por CPF/CNPJ · {negotiation.cliente_nome}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/15 rounded-full transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">

                    {/* Painel de busca */}
                    <div className={`${card} p-4 space-y-3 animate-slide-up`}>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <input
                                value={valorInput}
                                onChange={(e) => setValorInput(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && valorValido && !loading) openConfirm(false); }}
                                placeholder="CPF ou CNPJ"
                                className="flex-1 h-11 px-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 text-sm font-medium text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-[#5F6C37]/30 focus:border-[#5F6C37] transition-all"
                            />
                            <button
                                onClick={() => openConfirm(false)}
                                disabled={loading || !valorValido}
                                className="h-11 flex items-center justify-center gap-2 px-5 rounded-xl font-bold text-sm bg-gradient-to-r from-[#5F6C37] to-[#6d7c3f] hover:from-[#4d5a2c] hover:to-[#5F6C37] text-white shadow-sm hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                                Consultar
                            </button>
                        </div>

                        <div className="flex items-center gap-2 flex-wrap text-xs">
                            {result ? (
                                <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold bg-emerald-50 dark:bg-emerald-900/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/40">
                                    <CheckCircle2 size={13} />
                                    {result.source === 'cache' ? 'Dados já obtidos' : 'Consulta nova'}
                                    {result.cached_by ? ` · por ${result.cached_by}` : ''} {timeAgo(result.cached_at)}
                                </span>
                            ) : (
                                <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                                    <ShieldCheck size={13} /> Nenhuma consulta ainda
                                </span>
                            )}
                            {custo != null && (
                                <span className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg font-bold bg-amber-50 dark:bg-amber-900/10 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800/40">
                                    <Wallet size={12} /> {fmtBRL(custo)}/consulta
                                </span>
                            )}
                            {result && (
                                <button
                                    onClick={() => openConfirm(true)}
                                    disabled={loading}
                                    className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-slate-500 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all disabled:opacity-50"
                                    title="Forçar nova consulta (gera custo)"
                                >
                                    <RefreshCw size={13} /> Reconsultar
                                </button>
                            )}
                        </div>

                    </div>

                    {/* Confirmação de custo */}
                    {confirming && (
                        <div className={`${card} p-5 border-amber-200 dark:border-amber-800/40 animate-slide-up`}>
                            <div className="flex items-start gap-3">
                                <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg text-amber-600"><Wallet size={18} /></div>
                                <div className="flex-1">
                                    <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100">Confirmar consulta {forceMode ? '(forçada)' : ''}</h4>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                        Esta ação consulta a base da Lemit e {custo != null ? <>tem custo de <b>{fmtBRL(custo)}</b></> : 'pode gerar custo'}.
                                        {!forceMode && (apiCriterio === 'cpf' || apiCriterio === 'cnpj') && ' Se já houver consulta recente (24h), o resultado em cache é usado sem custo.'}
                                    </p>
                                    {history.length > 0 && (
                                        <div className="mt-3 text-[11px] text-slate-500 dark:text-slate-400">
                                            <p className="font-bold uppercase tracking-wide text-slate-400 mb-1">Consultas anteriores deste documento</p>
                                            <ul className="space-y-1 max-h-28 overflow-y-auto">
                                                {history.slice(0, 5).map((h) => (
                                                    <li key={h.id} className="flex items-center gap-2">
                                                        <Clock size={11} className="text-slate-300" />
                                                        {h.consultado_por_nome || 'Usuário'} · {timeAgo(h.created_at)}
                                                        <span className="text-slate-300">({h.source || 'api'})</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    <div className="flex justify-end gap-2 mt-4">
                                        <button onClick={() => setConfirming(false)} className="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 rounded-xl hover:bg-slate-200">
                                            Cancelar
                                        </button>
                                        <button onClick={() => runConsulta(forceMode)} className="px-4 py-2 text-xs font-bold text-white bg-[#5F6C37] hover:bg-[#4d5a2c] rounded-xl shadow-sm">
                                            Confirmar e consultar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Loading skeleton */}
                    {loading && (
                        <div className="flex flex-col items-center justify-center py-16 gap-3">
                            <Loader2 size={32} className="animate-spin text-primary" />
                            <p className="text-sm font-semibold text-slate-500">Consultando a base da Lemit...</p>
                        </div>
                    )}

                    {/* Erro */}
                    {error && !loading && (
                        <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800/40 text-sm font-semibold">
                            <AlertTriangle size={16} /> {error}
                        </div>
                    )}

                    {/* Resultado */}
                    {result && !loading && (
                        <>
                            {/* Dados cadastrais */}
                            <SectionCard icon={isEmpresa ? <Building2 size={14} /> : <User size={14} />} title="Dados cadastrais" tint="text-blue-700 bg-blue-50 dark:bg-blue-900/15 dark:text-blue-400" delay={20}>
                                {isEmpresa ? (
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                        <InfoCell label="Razão social" value={empresa?.razao_social} />
                                        <InfoCell label="Nome fantasia" value={empresa?.nome_fantasia} />
                                        <InfoCell label="CNPJ" value={formatDocumento(empresa?.cnpj)} />
                                        <InfoCell label="Situação" value={empresa?.situacao} />
                                        <InfoCell label="Tipo" value={empresa?.tipo} />
                                        <InfoCell label="Fundação" value={fmtDate(empresa?.data_fundacao)} />
                                        <InfoCell label="CNAE" value={empresa?.cnae ? `${empresa.cnae.numero || ''} ${empresa.cnae.descricao || ''}`.trim() : '—'} />
                                        <InfoCell label="Segmento CNAE" value={empresa?.cnae?.segmento} />
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                        <InfoCell label="Nome" value={pessoa?.nome} />
                                        <InfoCell label="CPF" value={formatDocumento(pessoa?.cpf)} />
                                        <InfoCell label="Nascimento" value={fmtDate(pessoa?.data_nascimento)} />
                                        <InfoCell label="Sexo" value={pessoa?.sexo} />
                                        <InfoCell label="Nome da mãe" value={pessoa?.nome_mae} />
                                        <InfoCell label="Situação CPF" value={pessoa?.situacao_cpf} />
                                        <InfoCell label="Renda estimada" value={pessoa?.renda != null ? fmtBRL(pessoa.renda) : '—'} />
                                        <InfoCell label="Ocupação" value={pessoa?.ocupacao} />
                                        <InfoCell label="Risco de crédito" value={pessoa?.risco_credito?.score_credito} />
                                        {pessoa?.falecido && <InfoCell label="Óbito" value={<span className="text-red-500 font-bold">Consta indicação</span>} />}
                                    </div>
                                )}
                            </SectionCard>

                            {/* Telefones */}
                            <SectionCard icon={<Phone size={14} />} title="Telefones" count={((result.data as any).celulares?.length || 0) + ((result.data as any).fixos?.length || 0)} tint="text-emerald-700 bg-emerald-50 dark:bg-emerald-900/15 dark:text-emerald-400" delay={40}>
                                {(() => {
                                    const tels: LemitTelefone[] = [ ...((result.data as any).celulares || []), ...((result.data as any).fixos || []) ];
                                    if (!tels.length) return <EmptyRow>Nenhum telefone retornado</EmptyRow>;
                                    return (
                                        <div className="flex flex-wrap gap-2">
                                            {tels.sort((a, b) => (a.ranking ?? 99) - (b.ranking ?? 99)).map((p, i) => (
                                                <div key={i} className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200/60 dark:border-slate-700">
                                                    <span className="font-mono font-bold text-sm text-slate-700 dark:text-slate-200">{formatPhone(p)}</span>
                                                    {p.whatsapp && <span className="px-1.5 py-0.5 text-[9px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 rounded">WhatsApp</span>}
                                                    {p.ranking === 1 && <span className="px-1.5 py-0.5 text-[9px] font-bold bg-primary/10 text-primary rounded">melhor</span>}
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })()}
                            </SectionCard>

                            {/* E-mails */}
                            <SectionCard icon={<Mail size={14} />} title="E-mails" count={(result.data as any).emails?.length || 0} tint="text-sky-700 bg-sky-50 dark:bg-sky-900/15 dark:text-sky-400" delay={60}>
                                {!(result.data as any).emails?.length ? <EmptyRow>Nenhum e-mail retornado</EmptyRow> : (
                                    <div className="flex flex-wrap gap-2">
                                        {(result.data as any).emails.map((e: any, i: number) => (
                                            <div key={i} className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200/60 dark:border-slate-700">
                                                <span className="text-sm text-slate-700 dark:text-slate-200">{e.email}</span>
                                                {e.ranking === 1 && <span className="px-1.5 py-0.5 text-[9px] font-bold bg-primary/10 text-primary rounded">melhor</span>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </SectionCard>

                            {/* Endereços */}
                            <SectionCard icon={<MapPin size={14} />} title="Endereços" count={isEmpresa ? (empresa?.endereco ? 1 : 0) : (pessoa?.enderecos?.length || 0)} tint="text-rose-700 bg-rose-50 dark:bg-rose-900/15 dark:text-rose-400" delay={80}>
                                {(() => {
                                    const ends = isEmpresa ? (empresa?.endereco ? [empresa.endereco] : []) : (pessoa?.enderecos || []);
                                    if (!ends.length) return <EmptyRow>Nenhum endereço retornado</EmptyRow>;
                                    return (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            {ends.map((e, i) => (
                                                <div key={i} className="px-3 py-2.5 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200/60 dark:border-slate-700 text-sm">
                                                    <p className="font-semibold text-slate-700 dark:text-slate-200">{e?.endereco || [e?.logradouro, e?.numero].filter(Boolean).join(', ') || '—'}</p>
                                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                                        {[e?.bairro, e?.cidade && e?.uf ? `${e.cidade}/${e.uf}` : e?.cidade, e?.cep].filter(Boolean).join(' · ')}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })()}
                            </SectionCard>

                            {/* Carros */}
                            {(result.data as any).carros?.length > 0 && (
                                <SectionCard icon={<Car size={14} />} title="Veículos" count={(result.data as any).carros.length} tint="text-indigo-700 bg-indigo-50 dark:bg-indigo-900/15 dark:text-indigo-400" delay={100}>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                            <thead><tr className="border-b border-slate-100 dark:border-slate-700/50 text-left text-[10px] uppercase text-slate-400 font-extrabold">
                                                <th className="px-3 py-2">Placa</th><th className="px-3 py-2">Marca/Modelo</th><th className="px-3 py-2">Ano</th>
                                            </tr></thead>
                                            <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                                                {(result.data as any).carros.map((c: any, i: number) => (
                                                    <tr key={i}>
                                                        <td className="px-3 py-2 font-mono font-bold text-slate-700 dark:text-slate-200">{c.placa || '—'}</td>
                                                        <td className="px-3 py-2 text-slate-600 dark:text-slate-300">{c.marca || '—'}</td>
                                                        <td className="px-3 py-2 text-slate-500">{[c.ano_fabricacao, c.ano_modelo].filter(Boolean).join('/') || '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </SectionCard>
                            )}

                            {/* Vínculos (pessoa) */}
                            {!isEmpresa && pessoa?.vinculos && pessoa.vinculos.length > 0 && (
                                <SectionCard icon={<Link2 size={14} />} title="Vínculos familiares" count={pessoa.vinculos.length} delay={120}>
                                    <div className="flex flex-wrap gap-2">
                                        {pessoa.vinculos.map((v, i) => (
                                            <div key={i} className="px-3 py-2 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200/60 dark:border-slate-700 text-xs">
                                                <span className="font-semibold text-slate-700 dark:text-slate-200">{v.nome_vinculo}</span>
                                                <span className="ml-2 px-1.5 py-0.5 bg-primary/10 text-primary rounded text-[9px] font-bold uppercase">{v.tipo_vinculo}</span>
                                            </div>
                                        ))}
                                    </div>
                                </SectionCard>
                            )}

                            {/* Participação societária (pessoa) */}
                            {!isEmpresa && pessoa?.participacao_societaria && pessoa.participacao_societaria.length > 0 && (
                                <SectionCard icon={<Building2 size={14} />} title="Participações societárias" count={pessoa.participacao_societaria.length} delay={140}>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                            <thead><tr className="border-b border-slate-100 dark:border-slate-700/50 text-left text-[10px] uppercase text-slate-400 font-extrabold">
                                                <th className="px-3 py-2">CNPJ</th><th className="px-3 py-2">Empresa</th><th className="px-3 py-2 text-center">Participação</th><th className="px-3 py-2 text-center">Situação</th>
                                            </tr></thead>
                                            <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                                                {pessoa.participacao_societaria.map((p, i) => (
                                                    <tr key={i}>
                                                        <td className="px-3 py-2 font-mono text-slate-600 dark:text-slate-300">{formatDocumento(p.cnpj)}</td>
                                                        <td className="px-3 py-2 font-bold text-slate-700 dark:text-slate-200">{p.nome}</td>
                                                        <td className="px-3 py-2 text-center font-bold text-primary">{p.participacao_socio != null ? `${p.participacao_socio}%` : '—'}</td>
                                                        <td className="px-3 py-2 text-center text-slate-500">{p.situacao_cadastral || '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </SectionCard>
                            )}

                            {/* Sócios (empresa) */}
                            {isEmpresa && empresa?.socios && empresa.socios.length > 0 && (
                                <SectionCard icon={<Users size={14} />} title="Quadro societário" count={empresa.socios.length} delay={140}>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                            <thead><tr className="border-b border-slate-100 dark:border-slate-700/50 text-left text-[10px] uppercase text-slate-400 font-extrabold">
                                                <th className="px-3 py-2">Nome</th><th className="px-3 py-2">CPF/CNPJ</th><th className="px-3 py-2 text-center">Participação</th>
                                            </tr></thead>
                                            <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                                                {empresa.socios.map((s, i) => (
                                                    <tr key={i}>
                                                        <td className="px-3 py-2 font-bold text-slate-700 dark:text-slate-200">{s.nome}</td>
                                                        <td className="px-3 py-2 font-mono text-slate-500">{formatDocumento(s.cpf)}</td>
                                                        <td className="px-3 py-2 text-center font-bold text-primary">{s.participacao != null ? `${s.participacao}%` : '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </SectionCard>
                            )}

                            {/* Revisar e importar */}
                            {importFields.length > 0 && (
                                <div className={`${card} border-primary/30 animate-slide-up`} style={{ animationDelay: '160ms' }}>
                                    <div className={sectionHead + ' bg-primary/[0.04]'}>
                                        <div className="p-1.5 rounded-lg bg-primary/10 text-primary"><Download size={14} /></div>
                                        <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200">Revisar e importar para o lead</h4>
                                        <span className="ml-auto text-[11px] font-semibold text-slate-400">{selectedCount} selecionado(s)</span>
                                    </div>
                                    <div className="p-4 space-y-2">
                                        {importFields.map((f) => (
                                            <label key={f.key} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer border border-transparent hover:border-slate-100 dark:hover:border-slate-700 transition-all">
                                                <input
                                                    type="checkbox"
                                                    checked={!!selected[f.key]}
                                                    onChange={(e) => setSelected((s) => ({ ...s, [f.key]: e.target.checked }))}
                                                    className="w-4 h-4 accent-[#5F6C37]"
                                                />
                                                <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-400 w-32 flex-shrink-0">{f.label}</span>
                                                <div className="flex items-center gap-2 min-w-0 flex-1 text-sm">
                                                    <span className={`truncate ${f.crmAtual ? 'text-slate-400' : 'text-slate-300 italic'}`}>{f.crmAtual || 'vazio'}</span>
                                                    <ChevronRight size={14} className="text-slate-300 flex-shrink-0" />
                                                    <span className="font-bold text-slate-700 dark:text-slate-200 truncate">{f.lemitValor}</span>
                                                </div>
                                                {f.crmAtual && f.crmAtual !== f.lemitValor && (
                                                    <span className="text-[9px] font-bold px-1.5 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded flex-shrink-0">substitui</span>
                                                )}
                                            </label>
                                        ))}
                                        <div className="flex justify-end pt-2">
                                            <button
                                                onClick={handleImport}
                                                disabled={importing || selectedCount === 0}
                                                className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm bg-[#5F6C37] hover:bg-[#4d5a2c] text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                            >
                                                {importing ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                                                Importar selecionados
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* JSON bruto */}
                            <div className={`${card} animate-slide-up`} style={{ animationDelay: '180ms' }}>
                                <button onClick={() => setShowRaw((v) => !v)} className="w-full flex items-center gap-2.5 px-5 py-3 text-left">
                                    <div className="p-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-500"><Code2 size={14} /></div>
                                    <span className="text-sm font-bold text-slate-700 dark:text-slate-200">Retorno completo (JSON)</span>
                                    <ChevronRight size={16} className={`ml-auto text-slate-400 transition-transform ${showRaw ? 'rotate-90' : ''}`} />
                                </button>
                                {showRaw && (
                                    <pre className="px-5 pb-4 text-[11px] leading-relaxed text-slate-600 dark:text-slate-300 overflow-x-auto max-h-72 overflow-y-auto">
                                        {JSON.stringify(result.raw ?? result.data, null, 2)}
                                    </pre>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default LemitConsultaModal;
